import type { ConnectionRegistry } from "./connection-registry";
import type { ServiceAuthAdapter, ServiceKey } from "./auth/types";
import { ServiceAuthError, ServiceNotConfiguredError } from "./auth/types";
import { emitApiTelemetry, emitNetworkError, perfNow, sanitisePath } from "../telemetry/apiTelemetry";
import { ensureAccess } from "./studio/accessGuard";

export interface ServiceFetchInit extends Omit<RequestInit, "headers"> {
  url: string;
  headers?: Record<string, string>;
}

function withAuth(
  init: ServiceFetchInit,
  adapter: ServiceAuthAdapter,
): RequestInit {
  return {
    ...init,
    headers: { ...init.headers, ...adapter.getAuthHeaders() },
  };
}

/**
 * Fetch wrapper that resolves the right adapter for `key`, attaches its
 * service-specific auth headers, and retries exactly once on 401 after
 * refreshing tokens. Concurrency-safety lives in the adapter (refresh()
 * is deduplicated there), so this wrapper stays small.
 *
 * Telemetry instrumentation:
 *   - Every call emits a `perf.api.call` event with status code and
 *     client-side duration. The path is templated (we drop the origin and
 *     query string) so we never log a real customer-id-in-URL.
 *   - 4xx and 5xx responses additionally emit `error.api.4xx` /
 *     `error.api.5xx` events for the error pivot.
 *   - Network failures emit `error.network`.
 */
export function createServiceFetch(
  registry: ConnectionRegistry,
  fetchImpl: typeof fetch = fetch,
) {
  return async function serviceFetch(
    key: ServiceKey,
    init: ServiceFetchInit,
  ): Promise<Response> {
    // Same pre-request access re-validation as fetchWithRefresh: a connector call
    // must also be blocked if the user was revoked or lost access mid-session.
    await ensureAccess();

    const adapter = registry.get(key);
    if (!adapter) throw new ServiceNotConfiguredError(key);

    if (!adapter.isConnected()) {
      const tokens = await adapter.silentConnect();
      if (!tokens) {
        throw new ServiceAuthError(key, "silent_connect_failed");
      }
    }

    const method = (init.method ?? "GET").toUpperCase();
    const templatedPath = sanitisePath(init.url);
    const started = perfNow();
    let res: Response;
    try {
      res = await fetchImpl(init.url, withAuth(init, adapter));
    } catch (err) {
      emitNetworkError(templatedPath, method, started, "integration", err);
      throw err;
    }

    if (res.status === 401) {
      await adapter.refresh();
      res = await fetchImpl(init.url, withAuth(init, adapter));
      if (res.status === 401) {
        emitApiTelemetry(templatedPath, method, started, res.status, "integration");
        throw new ServiceAuthError(key, "refresh_did_not_resolve_401");
      }
    }

    emitApiTelemetry(templatedPath, method, started, res.status, "integration");
    return res;
  };
}

export type ServiceFetch = ReturnType<typeof createServiceFetch>;
